package com.schoolconnect.app.repository.classsection

import com.schoolconnect.app.datasource.network.KtorApiClient
import com.schoolconnect.app.datasource.network.request.ClassSectionCreateRequest
import com.schoolconnect.app.datasource.network.request.ClassSectionWithSubjectsCreateRequest
import com.schoolconnect.app.datasource.network.request.ClassSectionFilterRequest
import com.schoolconnect.app.datasource.network.request.ClassSectionUpdateRequest
import com.schoolconnect.app.datasource.network.response.ClassSectionResponse
import com.schoolconnect.app.datasource.network.response.serverTotalCountOrNull
import com.schoolconnect.app.repository.academicyear.AcademicYearRepository
import com.schoolconnect.app.repository.common.CachedValue
import com.schoolconnect.app.repository.common.loadPagedItems
import com.schoolconnect.app.feature.common.PaginationPolicy
import com.schoolconnect.app.feature.common.ServerSort
import com.schoolconnect.app.repository.common.loadRefreshableCachedValue
import com.schoolconnect.app.repository.common.returnCachedAndRefresh
import com.schoolconnect.app.util.scLog
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.SupervisorJob

sealed interface ClassSectionScope {
    data object All : ClassSectionScope
    data class School(val schoolId: String) : ClassSectionScope
    data class AcademicYear(val academicYearId: String) : ClassSectionScope
}

data class ClassSectionsPageResult(
    val items: List<ClassSectionResponse>,
    val totalCount: Int? = null
)

data class ClassSectionAnalyticsResponse(
    val total: Int = 0,
    val active: Int = 0,
    val inactive: Int = 0
)

object ClassSectionRepository {
    private const val TAG = "[DATA_RETRIEVE_SC] ClassSectionRepository"
    private const val PAGE_SIZE = 100
    private const val MAX_PAGES = 100

    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)

    private data class CachedClassSectionsPage(
        val schoolId: String,
        val academicYearId: String,
        val query: String,
        val classId: String,
        val sectionId: String,
        val subjectCode: String,
        val status: String,
        val createdFrom: String,
        val createdTo: String,
        val pageIndex: Int,
        val pageSize: Int,
        val sort: String,
        val page: ClassSectionsPageResult
    )

    private data class CachedClassSectionAnalytics(
        val schoolId: String,
        val academicYearId: String,
        val analytics: CachedValue<ClassSectionAnalyticsResponse>
    )

    private var cachedPage: CachedClassSectionsPage? = null
    private var cachedAnalytics: CachedClassSectionAnalytics? = null
    private var cachedSchoolLookup: Pair<String, List<ClassSectionResponse>>? = null

    suspend fun loadSchoolClassSectionLookup(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<List<ClassSectionResponse>> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }
        cachedSchoolLookup?.takeIf { !forceRemote && it.first == normalizedSchoolId }?.let { cache ->
            scLog("$TAG loadSchoolClassSectionLookup cacheHit school=$normalizedSchoolId count=${cache.second.size}")
            return Result.success(cache.second)
        }

        scLog("$TAG loadSchoolClassSectionLookup start school=$normalizedSchoolId forceRemote=$forceRemote")
        return runCatching {
            loadPagedItems(
                tag = TAG,
                operation = "loadSchoolClassSectionLookup",
                pageSize = PAGE_SIZE,
                maxPages = MAX_PAGES,
                startContext = "school=$normalizedSchoolId"
            ) { offset ->
                KtorApiClient.filterClassSectionsWithSubjects(
                    request = ClassSectionFilterRequest(status = "active"),
                    sortBy = "class_name",
                    order = "asc",
                    limit = PAGE_SIZE,
                    offset = offset
                ).getOrElse { throw it }
            }.filter { it.schoolId == normalizedSchoolId && it.isActive != false }
                .distinctBy { it.id }
                .sortedWith(compareBy<ClassSectionResponse> { it.className.orEmpty() }.thenBy { it.sectionName.orEmpty() })
                .also { filtered ->
                    cachedSchoolLookup = normalizedSchoolId to filtered
                    scLog("$TAG loadSchoolClassSectionLookup complete school=$normalizedSchoolId count=${filtered.size}")
                }
        }.onFailure { throwable ->
            scLog("$TAG loadSchoolClassSectionLookup failure school=$normalizedSchoolId", throwable)
        }
    }

    suspend fun loadSchoolClassSectionsForAcademicYear(
        schoolId: String,
        academicYearId: String
    ): Result<List<ClassSectionResponse>> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedAcademicYearId = academicYearId.trim()
        if (normalizedSchoolId.isBlank() || normalizedAcademicYearId.isBlank()) {
            return Result.failure(IllegalArgumentException("School and academic year are required to load classes."))
        }
        scLog("$TAG loadSchoolClassSectionsForAcademicYear start school=$normalizedSchoolId academicYear=$normalizedAcademicYearId")
        return runCatching {
            loadPagedItems(
                tag = TAG,
                operation = "loadSchoolClassSectionsForAcademicYear",
                pageSize = PAGE_SIZE,
                maxPages = MAX_PAGES,
                startContext = "school=$normalizedSchoolId academicYear=$normalizedAcademicYearId"
            ) { offset ->
                KtorApiClient.getClassSections(
                    schoolId = normalizedSchoolId,
                    academicYearId = normalizedAcademicYearId,
                    isActive = true,
                    limit = PAGE_SIZE,
                    offset = offset,
                    sortBy = "class_name",
                    order = "asc"
                ).getOrElse { throw it }
            }.filter { it.schoolId == normalizedSchoolId && it.academicYearId == normalizedAcademicYearId && it.isActive != false }
                .distinctBy { it.id }
                .sortedWith(compareBy<ClassSectionResponse> { it.className.orEmpty() }.thenBy { it.sectionName.orEmpty() })
                .also { scLog("$TAG loadSchoolClassSectionsForAcademicYear success school=$normalizedSchoolId count=${it.size}") }
        }.onFailure { scLog("$TAG loadSchoolClassSectionsForAcademicYear failure school=$normalizedSchoolId academicYear=$normalizedAcademicYearId", it) }
    }

    suspend fun loadSchoolClassSectionsPage(
        schoolId: String,
        academicYearId: String,
        query: String = "",
        classId: String = "",
        sectionId: String = "",
        subjectCode: String = "",
        status: String = "",
        createdFrom: String = "",
        createdTo: String = "",
        pageIndex: Int = 0,
        pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
        sort: ServerSort = ServerSort("class_name", com.schoolconnect.app.feature.common.ServerSortOrder.ASC),
        forceRemote: Boolean = false
    ): Result<ClassSectionsPageResult> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedAcademicYearId = academicYearId.trim()
        if (normalizedSchoolId.isBlank() || normalizedAcademicYearId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId and academicYearId are required"))
        }

        val normalizedQuery = query.trim()
        val normalizedClassId = classId.trim()
        val normalizedSectionId = sectionId.trim()
        val normalizedSubjectCode = subjectCode.trim().uppercase()
        val normalizedStatus = status.trim().lowercase()
        val normalizedCreatedFrom = createdFrom.trim()
        val normalizedCreatedTo = createdTo.trim()
        val safePageIndex = pageIndex.coerceAtLeast(0)
        val safePageSize = pageSize.coerceAtLeast(1)
        val resolvedSearch = resolveClassSectionSearchFilters(
            query = normalizedQuery,
            classId = normalizedClassId,
            sectionId = normalizedSectionId,
            subjectCode = normalizedSubjectCode
        )
        val hasFilters = resolvedSearch.classId.isNotBlank() ||
            resolvedSearch.sectionId.isNotBlank() ||
            resolvedSearch.subjectCode.isNotBlank() ||
            normalizedSectionId.isNotBlank() ||
            normalizedStatus.isNotBlank() ||
            normalizedCreatedFrom.isNotBlank() ||
            normalizedCreatedTo.isNotBlank()

        cachedPage?.takeIf {
            !forceRemote &&
                it.schoolId == normalizedSchoolId &&
                it.academicYearId == normalizedAcademicYearId &&
                it.query == normalizedQuery &&
                it.classId == normalizedClassId &&
                it.sectionId == normalizedSectionId &&
                it.subjectCode == normalizedSubjectCode &&
                it.status == normalizedStatus &&
                it.createdFrom == normalizedCreatedFrom &&
                it.createdTo == normalizedCreatedTo &&
                it.pageIndex == safePageIndex &&
                it.pageSize == safePageSize &&
                it.sort == sort.cacheKey
        }?.let { cache ->
            scLog(
                "$TAG loadSchoolClassSectionsPage cacheHit school=$normalizedSchoolId academicYear=$normalizedAcademicYearId q=${normalizedQuery.ifBlank { "<blank>" }} classId=${normalizedClassId.ifBlank { "<blank>" }} sectionId=${normalizedSectionId.ifBlank { "<blank>" }} status=${normalizedStatus.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize count=${cache.page.items.size}"
            )
            return returnCachedAndRefresh(scope, TAG, "loadSchoolClassSectionsPage", "school=$normalizedSchoolId academicYear=$normalizedAcademicYearId pageIndex=$safePageIndex pageSize=$safePageSize", cache.page) {
                loadSchoolClassSectionsPage(
                    schoolId = normalizedSchoolId,
                    academicYearId = normalizedAcademicYearId,
                    query = query,
                    classId = classId,
                    sectionId = sectionId,
                    subjectCode = subjectCode,
                    status = status,
                    createdFrom = createdFrom,
                    createdTo = createdTo,
                    pageIndex = safePageIndex,
                    pageSize = safePageSize,
                    sort = sort,
                    forceRemote = true
                )
            }
        }

        val offset = safePageIndex * safePageSize
        scLog(
            "$TAG loadSchoolClassSectionsPage start school=$normalizedSchoolId academicYear=$normalizedAcademicYearId q=${normalizedQuery.ifBlank { "<blank>" }} classId=${normalizedClassId.ifBlank { "<blank>" }} sectionId=${normalizedSectionId.ifBlank { "<blank>" }} subjectCode=${normalizedSubjectCode.ifBlank { "<blank>" }} status=${normalizedStatus.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize offset=$offset forceRemote=$forceRemote"
        )

        return runCatching {
            val page = if (hasFilters) {
                val response = KtorApiClient.filterClassSectionsWithSubjects(
                    request = ClassSectionFilterRequest(
                        classId = resolvedSearch.classId.ifBlank { null },
                        sectionId = resolvedSearch.sectionId.uppercase().ifBlank { null },
                        subjectCode = resolvedSearch.subjectCode.uppercase().ifBlank { null },
                        status = normalizedStatus.ifBlank { null },
                        createdDateFrom = normalizedCreatedFrom.ifBlank { null },
                        createdDateTo = normalizedCreatedTo.ifBlank { null }
                    ),
                    sortBy = sort.normalizedField,
                    order = sort.order.apiValue,
                    limit = safePageSize,
                    offset = offset,
                    query = normalizedQuery.ifBlank { null }
                ).getOrElse { throw it }
                ClassSectionsPageResult(
                    items = response.distinctBy { it.id },
                    totalCount = response.serverTotalCountOrNull()
                )
            } else {
                // The Classes page needs the linked subject codes, so use the
                // documented class-section-subjects GET endpoint for its primary
                // list response. It accepts the active academic-year scope and
                // returns the same paginated class-section envelope with subjects.
                val response = KtorApiClient.getClassSectionsWithSubjects(
                    schoolId = normalizedSchoolId,
                    academicYearId = normalizedAcademicYearId,
                    isActive = null,
                    limit = safePageSize,
                    offset = offset,
                    sortBy = sort.normalizedField,
                    order = sort.order.apiValue
                ).getOrElse { throw it }
                // This documented GET endpoint is already scoped by the authenticated
                // school context and academic_year_id. Do not apply a second local ID
                // comparison: persisted context IDs can use a different representation
                // than the response while the server has already enforced the scope.
                val scopedRows = response
                ClassSectionsPageResult(
                    items = scopedRows.distinctBy { it.id },
                    totalCount = response.serverTotalCountOrNull()
                )
            }

            cachedPage = CachedClassSectionsPage(
                schoolId = normalizedSchoolId,
                academicYearId = normalizedAcademicYearId,
                query = normalizedQuery,
                classId = normalizedClassId,
                sectionId = normalizedSectionId,
                subjectCode = normalizedSubjectCode,
                status = normalizedStatus,
                createdFrom = normalizedCreatedFrom,
                createdTo = normalizedCreatedTo,
                pageIndex = safePageIndex,
                pageSize = safePageSize,
                sort = sort.cacheKey,
                page = page
            )
            scLog(
                "$TAG loadSchoolClassSectionsPage complete school=$normalizedSchoolId academicYear=$normalizedAcademicYearId count=${page.items.size} total=${page.totalCount?.toString() ?: "<unknown>"}"
            )
            page
        }.onFailure { throwable ->
            scLog(
                "$TAG loadSchoolClassSectionsPage failure school=$normalizedSchoolId academicYear=$normalizedAcademicYearId q=${normalizedQuery.ifBlank { "<blank>" }} pageIndex=$safePageIndex pageSize=$safePageSize",
                throwable
            )
        }
    }

    suspend fun loadSchoolClassSectionsAnalytics(
        schoolId: String,
        academicYearId: String,
        forceRemote: Boolean = false
    ): Result<ClassSectionAnalyticsResponse> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedAcademicYearId = academicYearId.trim()
        if (normalizedSchoolId.isBlank() || normalizedAcademicYearId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId and academicYearId are required"))
        }

        cachedAnalytics?.takeIf {
            !forceRemote &&
                it.schoolId == normalizedSchoolId &&
                it.academicYearId == normalizedAcademicYearId
        }?.let { cache ->
            scLog(
                "$TAG loadSchoolClassSectionsAnalytics cacheHit school=$normalizedSchoolId academicYear=$normalizedAcademicYearId total=${cache.analytics.value.total} active=${cache.analytics.value.active} inactive=${cache.analytics.value.inactive}"
            )
            return Result.success(cache.analytics.value)
        }

        return loadRefreshableCachedValue(
            scope = scope,
            tag = TAG,
            operation = "loadSchoolClassSectionsAnalytics",
            context = "school=$normalizedSchoolId academicYear=$normalizedAcademicYearId forceRemote=$forceRemote",
            cachedValue = if (forceRemote) null else cachedAnalytics?.takeIf {
                it.schoolId == normalizedSchoolId && it.academicYearId == normalizedAcademicYearId
            }?.analytics,
            fetchValue = {
                runCatching {
                    val rows = loadPagedItems(
                        tag = TAG,
                        operation = "loadSchoolClassSectionsAnalyticsRows",
                        pageSize = PAGE_SIZE,
                        maxPages = MAX_PAGES,
                        startContext = "school=$normalizedSchoolId academicYear=$normalizedAcademicYearId"
                    ) { offset ->
                        KtorApiClient.getClassSectionsWithSubjects(
                            schoolId = normalizedSchoolId,
                            academicYearId = normalizedAcademicYearId,
                            isActive = null,
                            limit = PAGE_SIZE,
                            offset = offset
                        ).getOrElse { throwable ->
                            scLog(
                                "$TAG loadSchoolClassSectionsAnalytics pageFailure school=$normalizedSchoolId academicYear=$normalizedAcademicYearId offset=$offset",
                                throwable
                            )
                            throw throwable
                        }
                    }.distinctBy { it.id }

                    ClassSectionAnalyticsResponse(
                        total = rows.size,
                        active = rows.count { it.isActive == true },
                        inactive = rows.count { it.isActive != true }
                    )
                }.fold(
                    onSuccess = { Result.success(it) },
                    onFailure = { Result.failure(it) }
                )
            },
            updateCache = { analytics, fetchedAt ->
                cachedAnalytics = CachedClassSectionAnalytics(
                    schoolId = normalizedSchoolId,
                    academicYearId = normalizedAcademicYearId,
                    analytics = CachedValue(analytics, fetchedAt)
                )
            },
            successDetails = { analytics ->
                "total=${analytics.total} active=${analytics.active} inactive=${analytics.inactive}"
            }
        )
    }

    suspend fun loadCurrentSchoolAcademicYearId(
        schoolId: String,
        forceRemote: Boolean = false
    ): Result<String> {
        val normalizedSchoolId = schoolId.trim()
        if (normalizedSchoolId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId is required"))
        }
        scLog("$TAG loadCurrentSchoolAcademicYearId start school=$normalizedSchoolId forceRemote=$forceRemote")
        return runCatching {
            val year = AcademicYearRepository.loadCurrentAcademicYear(normalizedSchoolId)
                .getOrElse { throw it }
                ?: throw IllegalStateException("Unable to resolve current academic year for school=$normalizedSchoolId")
            scLog(
                "$TAG loadCurrentSchoolAcademicYearId complete school=$normalizedSchoolId academicYearId=${year.id.orEmpty()}"
            )
            year.id.orEmpty()
        }.onFailure { throwable ->
            scLog("$TAG loadCurrentSchoolAcademicYearId failure school=$normalizedSchoolId", throwable)
        }
    }

    suspend fun createClassSection(request: ClassSectionCreateRequest): Result<ClassSectionResponse> {
        val normalizedSchoolId = request.schoolId.trim()
        val normalizedAcademicYearId = request.academicYearId.trim()
        if (normalizedSchoolId.isBlank() || normalizedAcademicYearId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId and academicYearId are required"))
        }
        scLog(
            "$TAG createClassSection start school=$normalizedSchoolId academicYear=$normalizedAcademicYearId className=${request.className.ifBlank { "<blank>" }} sectionName=${request.sectionName.ifBlank { "<blank>" }}"
        )
        return KtorApiClient.createClassSection(request).onSuccess { response ->
            invalidateCaches()
            scLog(
                "$TAG createClassSection success school=${response.schoolId.orEmpty().ifBlank { normalizedSchoolId }} academicYear=${response.academicYearId.orEmpty().ifBlank { normalizedAcademicYearId }} id=${response.id.orEmpty()}"
            )
        }.onFailure { throwable ->
            scLog("$TAG createClassSection failure school=$normalizedSchoolId academicYear=$normalizedAcademicYearId", throwable)
        }
    }

    suspend fun createClassSectionWithSubjects(
        schoolId: String,
        request: ClassSectionWithSubjectsCreateRequest
    ): Result<ClassSectionResponse> {
        val normalizedSchoolId = schoolId.trim()
        val normalizedAcademicYearId = request.academicYearId.trim()
        if (normalizedSchoolId.isBlank() || normalizedAcademicYearId.isBlank()) {
            return Result.failure(IllegalArgumentException("schoolId and academicYearId are required"))
        }
        scLog(
            "$TAG createClassSectionWithSubjects start school=$normalizedSchoolId academicYear=$normalizedAcademicYearId className=${request.className.ifBlank { "<blank>" }} sectionName=${request.sectionName.ifBlank { "<blank>" }} subjectCount=${request.subjectIds.size}"
        )
        return KtorApiClient.createClassSectionWithSubjects(
            schoolId = normalizedSchoolId,
            request = request
        ).onSuccess { response ->
            invalidateCaches()
            scLog(
                "$TAG createClassSectionWithSubjects success school=${response.schoolId.orEmpty().ifBlank { normalizedSchoolId }} academicYear=${response.academicYearId.orEmpty().ifBlank { normalizedAcademicYearId }} id=${response.id.orEmpty()} subjectLinks=${response.subjectLinkIds?.size ?: 0}"
            )
        }.onFailure { throwable ->
            scLog("$TAG createClassSectionWithSubjects failure school=$normalizedSchoolId academicYear=$normalizedAcademicYearId", throwable)
        }
    }

    suspend fun updateClassSection(
        classSectionId: String,
        request: ClassSectionUpdateRequest
    ): Result<ClassSectionResponse> {
        val normalizedId = classSectionId.trim()
        if (normalizedId.isBlank()) {
            return Result.failure(IllegalArgumentException("classSectionId is required"))
        }
        scLog("$TAG updateClassSection start classSectionId=$normalizedId")
        return KtorApiClient.updateClassSection(normalizedId, request).onSuccess { response ->
            invalidateCaches()
            scLog("$TAG updateClassSection success classSectionId=$normalizedId school=${response.schoolId.orEmpty()}")
        }.onFailure { throwable ->
            scLog("$TAG updateClassSection failure classSectionId=$normalizedId", throwable)
        }
    }

    suspend fun toggleClassSectionStatus(
        classSectionId: String,
        isActive: Boolean
    ): Result<ClassSectionResponse> {
        val normalizedId = classSectionId.trim()
        if (normalizedId.isBlank()) {
            return Result.failure(IllegalArgumentException("classSectionId is required"))
        }
        scLog("$TAG toggleClassSectionStatus start classSectionId=$normalizedId isActive=$isActive")
        return KtorApiClient.updateClassSection(
            classSectionId = normalizedId,
            request = ClassSectionUpdateRequest(isActive = isActive)
        ).onSuccess { response ->
            invalidateCaches()
            scLog("$TAG toggleClassSectionStatus success classSectionId=$normalizedId isActive=$isActive")
        }.onFailure { throwable ->
            scLog("$TAG toggleClassSectionStatus failure classSectionId=$normalizedId isActive=$isActive", throwable)
        }
    }

    suspend fun deleteClassSection(classSectionId: String): Result<Unit> {
        val normalizedId = classSectionId.trim()
        if (normalizedId.isBlank()) {
            return Result.failure(IllegalArgumentException("classSectionId is required"))
        }
        scLog("$TAG deleteClassSection start classSectionId=$normalizedId")
        return KtorApiClient.deleteClassSection(normalizedId).onSuccess {
            invalidateCaches()
            scLog("$TAG deleteClassSection success classSectionId=$normalizedId")
        }.onFailure { throwable ->
            scLog("$TAG deleteClassSection failure classSectionId=$normalizedId", throwable)
        }
    }

    fun invalidateCaches() {
        cachedPage = null
        cachedAnalytics = null
        cachedSchoolLookup = null
    }
}

internal data class ResolvedClassSectionSearchFilters(
    val classId: String = "",
    val sectionId: String = "",
    val subjectCode: String = ""
)

internal fun resolveClassSectionSearchFilters(
    query: String,
    classId: String,
    sectionId: String,
    subjectCode: String
): ResolvedClassSectionSearchFilters {
    val normalizedQuery = query.trim()
    if (normalizedQuery.isBlank()) {
        return ResolvedClassSectionSearchFilters(
            classId = classId.trim(),
            sectionId = sectionId.trim().uppercase(),
            subjectCode = subjectCode.trim().uppercase()
        )
    }

    val normalizedClassId = classId.trim()
    val normalizedSectionId = sectionId.trim()
    val normalizedSubjectCode = subjectCode.trim()
    if (normalizedClassId.isNotBlank() || normalizedSectionId.isNotBlank() || normalizedSubjectCode.isNotBlank()) {
        return ResolvedClassSectionSearchFilters(
            classId = normalizedClassId,
            sectionId = normalizedSectionId.uppercase(),
            subjectCode = normalizedSubjectCode.uppercase()
        )
    }

    // Treat the optional "class " prefix without regard to case, and only
    // classify numeric values as classes. Otherwise inputs such as "class a"
    // were sent as classId="a" even though A is a section code.
    val hasClassPrefix = normalizedQuery.startsWith("class ", ignoreCase = true)
    val queryWithoutClassPrefix = if (hasClassPrefix) {
        normalizedQuery.substringAfter(' ').trim()
    } else {
        normalizedQuery
    }
    val classSectionTokens = queryWithoutClassPrefix
        .split(Regex("\\s+|\\s*[-/]\\s*"))
        .filter { it.isNotBlank() }
    val numericClass = classSectionTokens.firstOrNull()?.takeIf { it.all(Char::isDigit) }
    if (numericClass != null) {
        return ResolvedClassSectionSearchFilters(
            classId = numericClass,
            sectionId = classSectionTokens.getOrNull(1).orEmpty().uppercase()
        )
    }

    if (queryWithoutClassPrefix.length <= 2 && queryWithoutClassPrefix.all(Char::isLetter)) {
        return ResolvedClassSectionSearchFilters(sectionId = queryWithoutClassPrefix.uppercase())
    }

    return ResolvedClassSectionSearchFilters(subjectCode = queryWithoutClassPrefix.uppercase())
}
